/**
 * Glue between a captured GPS track ({@link GeoStamp}[]) and the shared,
 * framework-agnostic kilometre math in `shared/sessions/km.ts`.
 *
 * This module owns NO math of its own — it composes `buildSegments`,
 * `sumBillableDrivingKm` and `validateTrack` so the Capacitor UI and the Expo
 * app stay on the single source of truth. Its only added value is wiring the
 * worker's manual "I'm driving / not driving" override (the hybrid-detection
 * requirement) into the per-segment override map the km module already accepts.
 */

import {
  applyBillableSanity,
  buildSegments,
  sumBillableDrivingKm,
  validateTrack,
  type SegmentOverrides,
  type TamperReport,
} from '@shared/sessions/km'
import type {
  DriveSegment,
  GeoStamp,
  TravelModeOverride,
} from '@shared/sessions/types'

export interface ComputeTrackKmOptions {
  /**
   * Manual hybrid override applied to EVERY segment of the track (the worker's
   * single "I'm driving / not driving" toggle). `null` (default) keeps the
   * automatic speed heuristic. Per-segment `overrides` take precedence over it.
   */
  manualOverride?: TravelModeOverride
  /** Optional fine-grained overrides keyed by the start-sample index. */
  overrides?: SegmentOverrides
  /** Override the driving speed threshold (km/h) for the heuristic. */
  drivingThresholdKmh?: number
}

export interface TrackKmResult {
  /** Classified, per-pair movement segments. */
  driveSegments: DriveSegment[]
  /** Sum of billable (driving) kilometres — what gets reimbursed. */
  kmTracked: number
  /** Anti-tamper / data-quality report for the raw track. */
  tamper: TamperReport
}

/**
 * Spread a single global override across every segment of a `length`-sample
 * track. Returns `undefined` when there is nothing to override so the km module
 * stays on its automatic path.
 */
function overridesForTrack(
  length: number,
  manualOverride: TravelModeOverride,
  explicit?: SegmentOverrides,
): SegmentOverrides | undefined {
  if (!manualOverride && !explicit) return undefined
  const map: SegmentOverrides = {}
  if (manualOverride) {
    // Segments are indexed by their `from` sample: 0 .. length - 2.
    for (let i = 0; i < length - 1; i += 1) {
      map[i] = manualOverride
    }
  }
  // Explicit per-segment overrides win over the blanket toggle.
  if (explicit) Object.assign(map, explicit)
  return map
}

/**
 * Turn a captured ordered GPS track into billable {@link DriveSegment}s + total
 * driving kilometres, and run anti-tamper validation. Pure and deterministic —
 * safe to call repeatedly for a live, in-progress estimate.
 */
export function computeTrackKm(
  samples: GeoStamp[],
  options: ComputeTrackKmOptions = {},
): TrackKmResult {
  const overrides = overridesForTrack(
    samples.length,
    options.manualOverride ?? null,
    options.overrides,
  )

  // Billable sanity (teleports, untracked gaps, jitter runs) applied here so
  // the live counter, the payload sent to the server, and the server's own
  // re-derivation all agree.
  const driveSegments = applyBillableSanity(
    buildSegments(samples, {
      drivingThresholdKmh: options.drivingThresholdKmh,
      overrides,
    }),
  )

  return {
    driveSegments,
    kmTracked: sumBillableDrivingKm(driveSegments),
    tamper: validateTrack(samples),
  }
}
